import { useCallback } from 'react';
import { Button, Group, List, Loader, Typography, Wrapper } from '@screentone/core';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';

import { useAlert } from 'contexts/alert/useAlert';
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { MultiListContainer, useMultiCollectionDtoPublishMutation } from 'data/generated/graphql';
import { useSelectedCollections } from 'features/multicollection-edit/contexts/SelectedCollectionsContext';
import { useConvertedProperty } from 'hooks';

export const Topbar = () => {
  const { alertSuccess, alertError } = useAlert();
  const currentProperty = useConvertedProperty();
  const queryClient = useQueryClient();
  const { mutateAsync, isLoading: isPublishing } = useMultiCollectionDtoPublishMutation();
  const isFetchingCollections = useIsFetching(['CollectionDTOById']) > 0;
  const { metadata, root, getRefDTO } = useDataModelContext<MultiListContainer>();
  const { selectedCheckboxes, allUnchecked, erroredCollections } = useSelectedCollections();

  const publishMultiCollection = useCallback(async () => {
    const erroredSelectedCollections = root.collection.filter(
      (c) => selectedCheckboxes[c.uuid] && erroredCollections[c.uuid]
    );

    if (erroredSelectedCollections.length > 0) {
      alertError(
        <Wrapper>
          There was an error while attempting to load the following collections. Please deselect them in the sidebar and
          reselect to retry loading them before publishing:
          <List listStyle="bullet">
            {erroredSelectedCollections.map((c) => (
              <List.Item key={c.uuid} margin={{ vertical: 'none' }}>
                {c.attributes.name}
              </List.Item>
            ))}
          </List>
          <br />
          (As a workaround if you want to proceed with publishing, you can deselect the failed collections to be able to
          publish the rest.)
        </Wrapper>
      );
      return;
    }

    const multiCollectionDTOWithRoot = getRefDTO(true);
    const multiCollectionDTO = getRefDTO(false);

    // Only send the collections that are selected for publishing
    multiCollectionDTOWithRoot.root.collection = (
      multiCollectionDTOWithRoot.root as MultiListContainer
    ).collection.filter((newswiresList) => selectedCheckboxes[newswiresList.uuid]);

    try {
      await mutateAsync({
        multiCollectionDTOPublishInput: {
          multiListContainer: multiCollectionDTOWithRoot.root,
          multiCollectionDTO,
          publicationKey: currentProperty!
        }
      });
    } catch (error) {
      if (error instanceof Error) {
        alertError(error.message);

        return;
      }

      alertError('An unexpected error occurred while trying to publish to Allesseh.');
      return;
    }

    void queryClient.invalidateQueries({ queryKey: ['CollectionDTOById'] });

    alertSuccess('Multiple collections successfully published');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProperty, erroredCollections, getRefDTO, mutateAsync, queryClient, root.collection, selectedCheckboxes]);

  return (
    <Group align="space-between" valign="center" margin={{ top: 'md', bottom: 'md', left: 'mlg' }}>
      <Typography variant="header2" margin={{ bottom: 'none' }}>
        {metadata.name}
      </Typography>
      <Button primary onClick={publishMultiCollection} disabled={allUnchecked || isPublishing || isFetchingCollections}>
        {isPublishing && <Loader size="md" margin={{ right: 'md' }} />}
        Publish
      </Button>
    </Group>
  );
};
